import { Buffer } from "buffer";
import { clientBranding } from "../config/clientConfig";
import { getDeviceIdentity } from "./deviceIdentity";

const ls = (key: string, fallback = "") =>
  (typeof localStorage !== "undefined" ? localStorage.getItem(key) : null) ?? fallback;

const parseResolution = (res: string) => {
  const m = String(res || "").match(/(\d{2,5})\s*x\s*(\d{2,5})/i);
  if (!m) return { w: "1920", h: "1080" };
  return { w: m[1], h: m[2] };
};

// EU/EEA + UK — GDPR (and UK GDPR) applicable territories. Used to derive
// AV_GDPR from the request's originating IP per Sofast's guidance
// ("AV_GDPR can be determined by your player based on the request's
// originating IP address"), instead of hardcoding "1" for all traffic.
const GDPR_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", // EU
  "IS", "LI", "NO", // EEA
  "GB", // UK GDPR
]);

interface GeoInfo {
  ip: string;
  countryCode: string;
}

// IP + country lookup is only used to build ad params — cache it for the life
// of the app instance so every channel switch doesn't re-hit the lookup service.
let cachedGeoInfo: Promise<GeoInfo> | null = null;
async function getGeoInfo(): Promise<GeoInfo> {
  if (cachedGeoInfo) return cachedGeoInfo;
  cachedGeoInfo = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2500);
      const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      return {
        ip: typeof data?.ip === "string" ? data.ip : "",
        countryCode: typeof data?.country_code === "string" ? data.country_code : "",
      };
    } catch (err) {
      console.warn("⚠ Geo/IP lookup failed:", err);
      return { ip: "", countryCode: "" };
    }
  })();
  return cachedGeoInfo;
}

interface EpgContentMeta {
  title: string;
  episode: string;
  season: string;
  category: string;
  keywords: string;
  contentId: string;
  channel: string;
  context: string;
  genre: string;
  language: string;
  rating: string;
  /** Epoch ms when this programme ends — used to expire the cache entry. */
  expiresAt: number;
}

const EPG_XML_TAG = (el: Element, tag: string) => el.getElementsByTagName(tag)[0]?.textContent?.trim() ?? "";

// EPG start/stop timestamps come as XMLTV-style "YYYYMMDDHHMMSS +ZZZZ".
function parseEpgTimestamp(raw: string | null): number {
  const m = String(raw || "").match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})\s*([+-]\d{4})?$/);
  if (!m) return NaN;
  const [, y, mo, d, h, mi, s, tz] = m;
  const offset = tz ? `${tz.slice(0, 3)}:${tz.slice(3)}` : "Z";
  return new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}${offset}`).getTime();
}

function extractEpgMeta(programmeEl: Element): EpgContentMeta {
  const titleEl = programmeEl.getElementsByTagName("title")[0] || null;
  const seasonNumber = programmeEl.querySelector("seasonsList > season > seasonNumber")?.textContent?.trim() ?? "";
  const stop = parseEpgTimestamp(programmeEl.getAttribute("stop"));
  const rawLang = titleEl?.getAttribute("lang") || "";

  return {
    title: EPG_XML_TAG(programmeEl, "title"),
    episode: EPG_XML_TAG(programmeEl, "episodeNumber"),
    season: seasonNumber,
    category: EPG_XML_TAG(programmeEl, "category"),
    keywords: EPG_XML_TAG(programmeEl, "tags"),
    contentId: EPG_XML_TAG(programmeEl, "programId") || EPG_XML_TAG(programmeEl, "assetId"),
    channel: programmeEl.getAttribute("channel") || "",
    context: EPG_XML_TAG(programmeEl, "type"),
    genre: EPG_XML_TAG(programmeEl, "genre"),
    // ISO 639-1: EPG lang attributes can list several ("EN,ES") — take the first.
    language: (rawLang.split(",")[0] || "").trim().toLowerCase(),
    rating: EPG_XML_TAG(programmeEl, "rating"),
    expiresAt: Number.isNaN(stop) ? Date.now() + 5 * 60 * 1000 : stop,
  };
}

// Sofast's ad-decisioning "AV_CONTENT_*" macros must reflect the programme
// actually airing right now (per Sofast's EPG-mapping guidance, 2026-09-14) —
// never hardcoded/guessed values. Cached per channel until the current
// programme's air window ends, so a channel zap doesn't always pay the fetch.
const epgMetaCache = new Map<string, { expiresAt: number; promise: Promise<EpgContentMeta | null> }>();

async function getEpgContentMeta(epgChannelId: string): Promise<EpgContentMeta | null> {
  if (!epgChannelId) return null;

  const cached = epgMetaCache.get(epgChannelId);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const promise = (async (): Promise<EpgContentMeta | null> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const res = await fetch(
        `https://epg.sofast.tv/api/ChannelXML/GetEpgXml?ChannelId=${encodeURIComponent(epgChannelId)}&DurationHours=24`,
        { signal: controller.signal },
      );
      clearTimeout(timeoutId);
      const xml = await res.text();
      const doc = new DOMParser().parseFromString(xml, "text/xml");

      const now = Date.now();
      const programmes = Array.from(doc.getElementsByTagName("programme"));
      const current = programmes.find((el) => {
        const start = parseEpgTimestamp(el.getAttribute("start"));
        const stop = parseEpgTimestamp(el.getAttribute("stop"));
        return !Number.isNaN(start) && !Number.isNaN(stop) && now >= start && now <= stop;
      });

      return current ? extractEpgMeta(current) : null;
    } catch (err) {
      console.warn("⚠ EPG lookup failed:", err);
      return null;
    }
  })();

  // Placeholder expiry until the fetch resolves and reports the programme's
  // real end time; avoids a stampede of parallel requests for the same channel.
  epgMetaCache.set(epgChannelId, { expiresAt: Date.now() + 60 * 1000, promise });
  promise
    .then((meta) => epgMetaCache.set(epgChannelId, { expiresAt: meta?.expiresAt ?? Date.now() + 5 * 60 * 1000, promise }))
    .catch(() => epgMetaCache.delete(epgChannelId));

  return promise;
}

function decodeBase64Utf8(value: string): string {
  if (!value) return "";
  try {
    return Buffer.from(value, "base64").toString("utf-8");
  } catch {
    try {
      return atob(value);
    } catch {
      return value;
    }
  }
}

// fast_channel EPG metadata comes from the fast-channel backend's JSON
// get_catchup_epg listings (see fetchFastChannelCatchupEpg below), not the
// Sofast XML feed — cached per stream_id until the current listing's
// stop_timestamp, same lifecycle as epgMetaCache.
const fastChannelEpgMetaCache = new Map<string, { expiresAt: number; promise: Promise<EpgContentMeta | null> }>();

async function getFastChannelEpgContentMeta(channel: any): Promise<EpgContentMeta | null> {
  const epgChannelId = channel?.epg_channel_id;

  if (epgChannelId === undefined || epgChannelId === null || epgChannelId === "") return null;
  const cacheKey = String(epgChannelId);

  const cached = fastChannelEpgMetaCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const promise = (async (): Promise<EpgContentMeta | null> => {
    try {
      const data = await fetchFastChannelCatchupEpg(epgChannelId, new Date());

      const listings: any[] = Array.isArray(data?.epg_listings) ? data.epg_listings : [];

      const nowSec = Math.floor(Date.now() / 1000);
      const current = listings.find((item) => {
        const start = Number(item?.start_timestamp);
        const stop = Number(item?.stop_timestamp);
        return !Number.isNaN(start) && !Number.isNaN(stop) && nowSec >= start && nowSec <= stop;
      });
      if (!current) return null;

      const stopMs = Number(current.stop_timestamp) * 1000;

      return {
        title: decodeBase64Utf8(current.title),
        episode: current?.episode ? decodeBase64Utf8(current.episode) : "",
        season: current?.season ? decodeBase64Utf8(current.season) : "",
        category: current?.category ? decodeBase64Utf8(current.category) : "",
        keywords: current?.keywords ? decodeBase64Utf8(current.keywords) : "",
        contentId: current.id || "",
        channel: current.channel_name || current.channel_id || "",
        context: current.type || "",
        genre: current?.genre ? decodeBase64Utf8(current.genre) : "",
        language: (String(current.lang || "en").split(",")[0] || "en").trim().toLowerCase(),
        rating: current?.rating ? decodeBase64Utf8(current.rating) : "",
        expiresAt: Number.isNaN(stopMs) ? Date.now() + 5 * 60 * 1000 : stopMs,
      };
    } catch (err) {
      console.warn("⚠ Fast channel EPG lookup failed:", err);
      return null;
    }
  })();

  // Placeholder expiry until the fetch resolves and reports the listing's
  // real end time; avoids a stampede of parallel requests for the same stream.
  fastChannelEpgMetaCache.set(cacheKey, { expiresAt: Date.now() + 60 * 1000, promise });
  promise
    .then((meta) => fastChannelEpgMetaCache.set(cacheKey, { expiresAt: meta?.expiresAt ?? Date.now() + 5 * 60 * 1000, promise }))
    .catch(() => fastChannelEpgMetaCache.delete(cacheKey));

  return promise;
}

/**
 * Append the ad-decisioning vendor's required "ads.AV_*" query parameters to a
 * stream URL. Existing query params (e.g. signed tokens) are preserved.
 */
export async function appendAvAdParams(inputUrl: string, channel?: any): Promise<string> {
  if (!inputUrl) return inputUrl;

  let u: URL;
  try {
    u = new URL(inputUrl);
  } catch (e) {
    console.warn("❌ appendAvAdParams: invalid URL", e);
    return inputUrl;
  }

  const p = u.searchParams;
  const set = (key: string, value: unknown) => {
    const s = value === undefined || value === null ? "" : String(value).trim();
    if (!s) {
      p.delete(`ads.${key}`);
      return;
    }
    p.set(`ads.${key}`, s);
  };
  // Unlike set() above, AV_CONTENT_* params must always be present in the
  // URL — even blank — so the ad vendor sees the field was considered rather
  // than the query param being absent entirely.
  const setAlways = (key: string, value: unknown) => {
    const s = value === undefined || value === null ? "" : String(value).trim();
    p.set(`ads.${key}`, s);
  };

  const bridge = typeof window !== "undefined" ? (window as any).AndroidBridge : undefined;
  // getDeviceIdentity()'s return value isn't used directly below — it's awaited
  // for its side effect of populating the "android_id" localStorage cache that
  // AV_IDFA reads from (see deviceIdentity.ts's getAndroidIdWithRetry).
  const isFastChannel = channel?.channel_type === "fast_channel";
  const [, { ip, countryCode }, epgMeta] = await Promise.all([
    getDeviceIdentity(),
    getGeoInfo(),
    isFastChannel ? getFastChannelEpgContentMeta(channel) : getEpgContentMeta(channel?.epg_channel_id || ""),
  ]);

  const screenRes = bridge?.getScreenResolution?.() || ls("screen_resolution", "1920x1080");
  const { w, h } = parseResolution(screenRes);

  const appBundle = ls("app_bundle", clientBranding().app_bundle);
  const appName = ls("app_name", clientBranding().appName);
  // Must be the store-listed developer domain (where app-ads.txt lives), NOT
  // the domain hosting the WebView bundle — SSPs verify inventory against the
  // former per Sofast's app-ads.txt confirmation (2026-08-26).
  const appDomain = ls("app_domain", clientBranding().developerDomain);
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const language = typeof navigator !== "undefined" ? navigator.language || "en" : "en";
  const dnt = typeof navigator !== "undefined" && navigator.doNotTrack === "1" ? "1" : "0";

  const now = Math.floor(Date.now() / 1000);

  // -------- cache-bust / timing --------
  set("cb", now);
  set("AV_TIMESTAMP", now);

  // -------- player / device --------
  set("AV_WIDTH", w);
  set("AV_HEIGHT", h);
  // AV_URL is the app's website URL per Sofast's spec (not the stream/manifest
  // URL) — left empty if there's no developer domain configured.
  set("AV_URL", "https://evomedia.pt/");
  set("AV_DOMAIN", appDomain);
  set("AV_HOST", appDomain);
  set("AV_APP_DOMAIN", appDomain);
  set("AV_APPNAME", appName);
  set("AV_APPPKGNAME", appBundle);
  set("AV_APPSTOREURL", `https://play.google.com/store/apps/details?id=${appBundle}`);
  set("AV_USER_AGENT", userAgent);
  set("AV_OS", "Android");
  set("AV_OSVERS", bridge?.getOsVersion?.() || ls("os_version", ""));
  set("AV_MAKE", bridge?.getBrand?.() || bridge?.getManufacturer?.() || ls("device_brand", ""));
  set("AV_MODEL", bridge?.getModel?.() || ls("device_model", ""));
  set("AV_IP", ip);
  // AV_LATITUDE/AV_LONGITUDE are mandatory per Sofast's spec, but most Android
  // TV boxes never get a location fix. Rather than send these blank, fall back
  // to a static coordinate for Lisbon, Portugal — the confirmed primary
  // traffic market — overridable via localStorage for other markets.
  set("AV_LATITUDE", bridge?.getLatitude?.() || ls("ads_latitude", "38.7223"));
  set("AV_LONGITUDE", bridge?.getLongitude?.() || ls("ads_longitude", "-9.1393"));
  // ISO 639-1: navigator.language is a full locale tag (e.g. "en-GB") — Sofast
  // wants just the two-letter language code.
  set("AV_LANGUAGE", language.split("-")[0]?.toLowerCase() || "en");
  set("AV_DNT", dnt);
  set("AV_LMT", ls("ads_lmt", "0"));

  // Standard IAB video-instream placement — this app only ever serves
  // full-screen instream live/VOD, so these are fixed, not per-request.
  set("AV_CATEGORY", "IAB1");
  set("AV_PLCMT", "1");
  set("AV_PLACEMENT", "1");

  // -------- ad identifiers --------
  // getDeviceIdentity() above already triggers the native Android ID lookup
  // and caches its result, so read it back from storage here rather than
  // re-running the (async) retry loop synchronously.
  const adIdfa = ls("android_id", "");

  set("AV_IDFA", adIdfa);
  // "aaid" is the IAB-standard value for an Android/Android TV device
  // identifier (Sofast rejected our previous "gaid"/"androidid" labels).
  set("AV_IFA_TYPE", "aaid");

  // -------- privacy / consent --------
  // Per Sofast's guidance, AV_GDPR is derived from the request's originating
  // IP address rather than hardcoded — "ads_gdpr" in localStorage can still
  // force an override for testing. No IAB TCF/CMP is wired up yet, and per
  // Sofast, a TCF consent string is only expected for GDPR traffic, so
  // AV_CONSENT stays empty (no CMP dialog is shown for non-GDPR markets
  // either). AV_CCPA is US-only and omitted (empty) for this EU/UK traffic.
  const isGdprRegion = countryCode ? GDPR_COUNTRY_CODES.has(countryCode.toUpperCase()) : true;
  set("AV_GDPR", ls("ads_gdpr", isGdprRegion ? "1" : "0"));
  set("AV_CONSENT", ls("ads_gdpr_consent", ""));
  set("AV_CCPA", ls("ads_us_privacy", ""));

  // -------- supply chain --------
  // ads.AV_SCHAIN is listed as a mandatory common param in Sofast's
  // 2026-08-20 confirmation; left empty (omitted) until we have a real
  // IAB schain object to send.
  set("AV_SCHAIN", ls("ads_schain", ""));

  // -------- content metadata --------
  // Sofast requires AV_CONTENT_* to reflect the programme actually airing on
  // this channel right now, mapped from their EPG feed (GetEpgXml) — never a
  // hardcoded/guessed value. AV_CONTENT_URL stays empty per Sofast's spec;
  // AV_CONTENT_SERIES/AV_CONTENT_NETWORK stay empty per Sofast's own mapping
  // script (2026-09-14), which doesn't source them from the EPG either.
  setAlways("AV_CONTENT_URL", "");
  setAlways("AV_CONTENT_TITLE", epgMeta?.title || "");
  setAlways("AV_CONTENT_ID", epgMeta?.contentId || "");
  setAlways("AV_CONTENT_CHANNEL", epgMeta?.channel || "");
  setAlways("AV_CONTENT_CAT", epgMeta?.category || "");
  setAlways("AV_CONTENT_CONTEXT", epgMeta?.context || "");
  setAlways("AV_CONTENT_LANGUAGE", epgMeta?.language || "en");
  setAlways("AV_CONTENT_EPISODE", epgMeta?.episode || "");
  setAlways("AV_CONTENT_SEASON", epgMeta?.season || "");
  setAlways("AV_CONTENT_SERIES", "");
  setAlways("AV_CONTENT_GENRE", epgMeta?.genre || "");
  setAlways("AV_CONTENT_KEYWORDS", epgMeta?.keywords || "");
  setAlways("AV_CONTENT_NETWORK", "");
  setAlways("AV_CONTENT_RATING", epgMeta?.rating || "");

  return u.toString();
}

/**
 * Kick off the AV_IP lookup and device-identity fetch without waiting on them,
 * so their results are already cached by the time the first stream loads
 * (avoids adding IP-lookup latency to the first channel switch).
 */
export function warmAdParamCaches(): void {
  getGeoInfo().catch(() => { });
  getDeviceIdentity().catch(() => { });
}

/**
 * Build a fast_channel stream URL by appending all required ad params.
 * Strips any existing query string from directSource (server may send placeholders),
 * then rebuilds params from real device/app values.
 */
export async function buildFastChannelUrl(directSource: string, channel?: any): Promise<string> {
  if (!directSource) return directSource;

  const baseUrl = directSource.includes("?")
    ? directSource.substring(0, directSource.indexOf("?"))
    : directSource;

  return appendAvAdParams(baseUrl, channel);
}

/**
 * Build the fast channel URL, fire a GET request to initialize the
 * MediaTailor ad session, then return the URL for playback.
 */
export async function initFastChannelSession(directSource: string, channel?: any): Promise<string> {
  const url = await buildFastChannelUrl(directSource, channel);
  // Prime the MediaTailor ad session in the background — playback doesn't need
  // to wait on this round-trip, and it still succeeds even if this fails.
  fetch(url, { method: "GET" })
    .then(() => console.log("📡 Fast channel session initialized:", url))
    .catch((err) => console.warn("⚠ Fast channel session fetch failed (will still play):", err));
  return url;
}

const FAST_CHANNEL_BACKEND_URL = "https://innplay-apps-backend-229197090060.europe-west1.run.app";

/**
 * Format a Date as "YYYY-MM-DD HH:mm:ss GMT ±H" (device-local time/offset),
 * with spaces encoded as %20 to match get_catchup_epg's expected selected_day
 * format, e.g. "2025-08-19%2013:10:00%20GMT%20+2".
 */
export function formatSelectedDay(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");

  const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);

  return `${dateStr}%20${timeStr}%20GMT%20${sign}${offsetHours}`;
}

/**
 * Fetch catchup EPG listings for a fast_channel from the fast-channel backend
 * (separate from the regular playlist's get_catchup_epg, which uses playListUrl()).
 */
export async function fetchFastChannelCatchupEpg(streamId: string | number, date: Date): Promise<any> {

  const selectedDay = formatSelectedDay(date);
  const url = `${FAST_CHANNEL_BACKEND_URL}/player_api.php?action=get_catchup_epg&stream_id=${streamId}&selected_day=${selectedDay}`;

  const accessToken = ls("access_token");
  const response = await fetch(url, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
  });

  if (!response.ok) {
    throw new Error(`Fast channel catchup EPG fetch failed: ${response.status}`);
  }
  return response.json();
}
